import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Loader } from '../../components/ui/loader';

// icons
import { UserSquare, Shield, Activity, Clock } from 'iconsax-react';

// services
import roleService from '../../services/roleService';

// utils
import { getPermissionErrorMessage } from '../../utils/permissionMessages';

const RoleAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('Fetching analytics...');
      const result = await roleService.getRoleStats();
      console.log('Full API response:', result);
      
      if (result.success) {
        console.log('Setting analytics data:', result.data);
        setAnalytics(result.data);
      } else {
        console.error('API error:', result.message);
        setError(result.message || 'Failed to load analytics');
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      if (error.response?.data?.error?.code === 'INSUFFICIENT_PERMISSIONS') {
        const friendlyMessage = getPermissionErrorMessage(error.response.data.error.message);
        setError(friendlyMessage);
      } else {
        setError('Failed to load role analytics');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader className="h-[calc(100vh-200px)] w-full" />;
  }

  if (!analytics) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>{error || 'No analytics data available'}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const { roleDistribution = {}, userStats = {}, recentActivity = [], permissionUsage = {}, summary = {} } = analytics || {};

  // Prepare chart data
  const roleChartData = Object.entries(roleDistribution || {})
    .map(([roleName, data]) => ({
      name: data?.displayName || roleName,
      value: data?.count || 0,
      isSystem: data?.isSystem || false
    }))
    .sort((a, b) => b.value - a.value);

  const topPermissions = Object.entries(permissionUsage || {})
    .sort(([,a], [,b]) => (b || 0) - (a || 0))
    .slice(0, 10)
    .map(([permission, count]) => ({
      permission: permission.replace(/\./g, ' ').toUpperCase(),
      count: count || 0
    }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">Role Usage Analytics</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Comprehensive analytics and insights for role and permission management
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Card 1: Total Roles */}
        <Card className="hover:shadow-md transition-all duration-200 border-border">
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <div className="text-3xl font-extrabold text-blue-600 dark:text-blue-400">
                {summary.totalRoles || 0}
              </div>
              <p className="text-sm font-medium text-muted-foreground mt-1">
                Total Roles
              </p>
            </div>
            <div className="p-3 rounded-full bg-blue-50 dark:bg-blue-950/30 text-blue-600 dark:text-blue-400">
              <Shield size={24} />
            </div>
          </CardContent>
        </Card>

        {/* Card 2: System Roles */}
        <Card className="hover:shadow-md transition-all duration-200 border-border">
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <div className="text-3xl font-extrabold text-violet-600 dark:text-violet-400">
                {summary.systemRoles || 0}
              </div>
              <p className="text-sm font-medium text-muted-foreground mt-1">
                System Roles
              </p>
            </div>
            <div className="p-3 rounded-full bg-violet-50 dark:bg-violet-950/30 text-violet-600 dark:text-violet-400">
              <Shield size={24} />
            </div>
          </CardContent>
        </Card>

        {/* Card 3: Custom Roles */}
        <Card className="hover:shadow-md transition-all duration-200 border-border">
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <div className="text-3xl font-extrabold text-zinc-600 dark:text-zinc-400">
                {summary.customRoles || 0}
              </div>
              <p className="text-sm font-medium text-muted-foreground mt-1">
                Custom Roles
              </p>
            </div>
            <div className="p-3 rounded-full bg-zinc-100 dark:bg-zinc-800/50 text-zinc-600 dark:text-zinc-400">
              <Shield size={24} />
            </div>
          </CardContent>
        </Card>

        {/* Card 4: Total Users */}
        <Card className="hover:shadow-md transition-all duration-200 border-border">
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <div className="text-3xl font-extrabold text-sky-600 dark:text-sky-400">
                {userStats.total || 0}
              </div>
              <p className="text-sm font-medium text-muted-foreground mt-1">
                Total Users
              </p>
            </div>
            <div className="p-3 rounded-full bg-sky-50 dark:bg-sky-950/30 text-sky-600 dark:text-sky-400">
              <UserSquare size={24} />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Role Distribution */}
          <Card className="border-border shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-foreground">Role Distribution</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-4">
              {roleChartData.map((role) => {
                const percentage = userStats.total > 0 ? (role.value / userStats.total) * 100 : 0;
                return (
                  <div key={role.name} className="space-y-1.5">
                    <div className="flex justify-between text-sm font-medium">
                      <span className="text-foreground">{role.name}</span>
                      <span className="text-muted-foreground">{role.value} users</span>
                    </div>
                    {/* Progress bar */}
                    <div className="w-full bg-secondary rounded-full h-2">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${
                          role.isSystem ? "bg-primary" : "bg-zinc-400 dark:bg-zinc-600"
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {role.isSystem ? 'System Role' : 'Custom Role'}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Role Management */}
          <Card className="border-border shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-foreground">Role Management</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <p className="text-sm text-muted-foreground mb-4">
                Need to create, edit, or manage roles and permissions? Access the full role management interface.
              </p>
              <Button
                className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-md px-6"
                onClick={() => window.location.href = '/roles'}
              >
                Manage Roles
              </Button>
            </CardContent>
          </Card>

          {/* Key Insights */}
          <Card className="border-border shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-foreground">Key Insights</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Most Used Role */}
                <div className="p-4 bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/30 rounded-lg">
                  <span className="text-xs font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 block mb-1">
                    Most Used Role
                  </span>
                  <span className="text-lg font-bold text-foreground block truncate">
                    {roleDistribution?.[summary?.mostUsedRole]?.displayName || 'N/A'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {roleDistribution?.[summary?.mostUsedRole]?.count || 0} users
                  </span>
                </div>

                {/* Least Used Role */}
                <div className="p-4 bg-amber-50/50 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-900/30 rounded-lg">
                  <span className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 block mb-1">
                    Least Used Role
                  </span>
                  <span className="text-lg font-bold text-foreground block truncate">
                    {roleDistribution?.[summary?.leastUsedRole]?.displayName || 'N/A'}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {roleDistribution?.[summary?.leastUsedRole]?.count || 0} users
                  </span>
                </div>

                {/* Active Users */}
                <div className="p-4 bg-blue-50/50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/30 rounded-lg">
                  <span className="text-xs font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-400 block mb-1">
                    Active Users
                  </span>
                  <span className="text-lg font-bold text-foreground block truncate">
                    {userStats?.active || 0} / {userStats?.total || 0}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {userStats?.total ? Math.round(((userStats?.active || 0) / userStats.total) * 100) : 0}% active rate
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Top Permission Usage */}
          <Card className="border-border shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-foreground">Top Permission Usage</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0 space-y-4">
              {topPermissions.slice(0, 8).map((perm, index) => {
                const percentage = userStats.total > 0 ? (perm.count / userStats.total) * 100 : 0;
                const colors = [
                  'bg-blue-500',
                  'bg-emerald-500',
                  'bg-amber-500',
                  'bg-rose-500',
                  'bg-violet-500',
                  'bg-indigo-500',
                  'bg-cyan-500',
                  'bg-pink-500'
                ];
                const barColor = colors[index % colors.length];

                return (
                  <div key={perm.permission} className="space-y-1">
                    <div className="flex justify-between text-xs font-semibold">
                      <span className="text-muted-foreground truncate max-w-[80%]">{perm.permission}</span>
                      <span className="text-foreground">{perm.count}</span>
                    </div>
                    <div className="w-full bg-secondary rounded-full h-1.5">
                      <div
                        className={`h-full rounded-full transition-all duration-300 ${barColor}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="border-border shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-foreground">Recent Activity</CardTitle>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <div className="divide-y divide-border">
                {(recentActivity || []).slice(0, 10).map((activity, index) => {
                  let iconColorClass = "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400";
                  let IconComponent = Shield;

                  if (activity.action === 'ROLE_ASSIGNED') {
                    iconColorClass = "bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400";
                    IconComponent = UserSquare;
                  } else if (activity.action === 'ROLE_CREATED') {
                    iconColorClass = "bg-violet-50 text-violet-600 dark:bg-violet-950/40 dark:text-violet-400";
                    IconComponent = Shield;
                  } else if (activity.action === 'ROLE_UPDATED') {
                    iconColorClass = "bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400";
                    IconComponent = Activity;
                  }

                  return (
                    <div key={index} className="py-3 flex items-start gap-4 first:pt-0 last:pb-0">
                      <div className={`p-2 rounded-lg shrink-0 ${iconColorClass}`}>
                        <IconComponent size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-foreground">
                          {(activity?.action || '').replace(/_/g, ' ')}
                        </p>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mt-0.5">
                          <span className="text-xs text-muted-foreground truncate">
                            {activity?.userEmail || 'Unknown'}
                          </span>
                          <span className="hidden sm:inline text-xs text-muted-foreground">•</span>
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock size={12} />
                            {activity?.timestamp ? new Date(activity.timestamp).toLocaleString() : 'Unknown time'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {(!recentActivity || recentActivity.length === 0) && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No recent activity recorded
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RoleAnalytics;